import { useEffect } from "react";

import { useSetRecoilState } from "recoil";

import {
    LensCreatorPayoutEligibility,
    type MyLensesPreviewMedia,
    type PreviewMediaReference,
    Visibility,
} from "../../gql-generated/types";
import { type GetLensInfoResponse } from "../../lens/api/queries";
import { isVideoSrcExtension } from "../../lens-preview/validation";
import {
    DEFAULT_PREVIEW_NAME,
    type LensPreviewMediaItem,
    discoverabilityById,
    isGameUserProvidedById,
    lensCreatorPayoutEnrolledById,
    lensCtaAtomById,
    lensDescriptionAtomById,
    lensPreviewMediaItemsAtomById,
    lensSpectaclesTagsAtomById,
    lensSurfaceTypeAtomById,
    lensTagsAtomById,
    lensVisibilityAtomById,
} from "../../shared/state";
import { type PreSubmissionInfoFragment } from "../api/fragments";

import { makeDiscoverabilityForUpdate } from "./makeDiscoverabilityForUpdate";
import { makeLensCtaFromLensForUpdate } from "./makeLensCtaFromLensForUpdate";

const isMediaFromPresubmission = (
    media: MyLensesPreviewMedia | PreviewMediaReference,
): media is PreviewMediaReference => {
    return "signedUrl" in media && "uploadRefId" in media;
};

export const useMergeLensForUpdateWithPresubmissionInfo = (
    submissionId: string,
    lensForUpdate: GetLensInfoResponse | undefined,
    preSubmissionInfo: PreSubmissionInfoFragment | undefined,
    // TODO(interop-ph3): revise visibility
    defaultVisibility: Visibility = Visibility.VisibilityDontPublishToSnapchat,
): void => {
    const setTagsList = useSetRecoilState(lensTagsAtomById(submissionId));
    const setSpectaclesTagsList = useSetRecoilState(lensSpectaclesTagsAtomById(submissionId));
    const setLensCta = useSetRecoilState(lensCtaAtomById(submissionId));
    const setVisibility = useSetRecoilState(lensVisibilityAtomById(submissionId));
    const setPreviewMediaItems = useSetRecoilState(lensPreviewMediaItemsAtomById(submissionId));
    const setDiscoverability = useSetRecoilState(discoverabilityById(submissionId));
    const setIsGameUserProvided = useSetRecoilState(isGameUserProvidedById(submissionId));
    const setLensCreatorPayoutEnrolled = useSetRecoilState(lensCreatorPayoutEnrolledById(submissionId));
    const setLensDescription = useSetRecoilState(lensDescriptionAtomById(submissionId));
    const setSurfaceType = useSetRecoilState(lensSurfaceTypeAtomById(submissionId));

    // Data that can be modified on My Lenses only or Lens Studio Only
    useEffect(() => {
        if (lensForUpdate?.lens) {
            const { lensCreatorPayoutEligibility, description, spectaclesTags, isGameUserProvided } =
                lensForUpdate.lens;

            setLensCta(makeLensCtaFromLensForUpdate(lensForUpdate));
            setDiscoverability(makeDiscoverabilityForUpdate(lensForUpdate));
            setIsGameUserProvided(isGameUserProvided);
            setLensDescription(description);
            setLensCreatorPayoutEnrolled(
                lensCreatorPayoutEligibility !== LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset,
            );
            setSpectaclesTagsList(spectaclesTags ?? []);
        }

        // default visibility is computed in useLensSubmissionVisibilityConfig - respects roles, permissions and filter
        setVisibility(defaultVisibility);

        if (preSubmissionInfo?.surfaceType) {
            setSurfaceType(preSubmissionInfo.surfaceType);
        }
    }, [
        setTagsList,
        setLensCta,
        setVisibility,
        preSubmissionInfo,
        lensForUpdate,
        setDiscoverability,
        setIsGameUserProvided,
        setLensCreatorPayoutEnrolled,
        defaultVisibility,
        setLensDescription,
        setSpectaclesTagsList,
        setSurfaceType,
    ]);

    // Data that can be modified in Lens Studio and on My Lenses
    useEffect(() => {
        // data from Lens Studio takes precedence
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- testing for missing list
        const previewMediaForEdit =
            preSubmissionInfo?.previewMediaRefs.length === 0
                ? lensForUpdate?.lens?.previewMediaList
                : preSubmissionInfo?.previewMediaRefs;

        if (previewMediaForEdit && previewMediaForEdit.length > 0) {
            setPreviewMediaItems(
                previewMediaForEdit.map((media) => {
                    const common = {
                        file: null,
                        name: DEFAULT_PREVIEW_NAME,
                    };
                    let res: LensPreviewMediaItem;
                    if (isMediaFromPresubmission(media)) {
                        res = {
                            ...common,
                            url: media.signedUrl,
                            isVideo: isVideoSrcExtension(media.signedUrl),
                            type: "reference",
                            referenceId: media.uploadRefId,
                            isLensStudioPreview: true,
                        };
                    } else {
                        res = {
                            ...common,
                            url: media.standardMediaUrl,
                            isVideo: isVideoSrcExtension(media.standardMediaUrl),
                            type: "media",
                            mediaId: media.mediaId,
                            isLensStudioPreview: false,
                        };
                    }
                    return res;
                }),
            );
        }

        const presubmissionTagsList = preSubmissionInfo?.trendingTagsList;
        const existingLensTagsList = lensForUpdate?.lens?.tagsList;

        const tagsListForEdit =
            presubmissionTagsList && presubmissionTagsList.length > 0 ? presubmissionTagsList : existingLensTagsList;
        if (tagsListForEdit) {
            setTagsList(tagsListForEdit);
        }
    }, [lensForUpdate, preSubmissionInfo, setTagsList, setPreviewMediaItems]);
};
